import type { ReactNode } from 'react'
import { X } from 'lucide-react'
import {
  nvBackdropClass,
  nvPanelClass,
  nvIconButtonClass,
  nvAddModalSubtitleClass,
} from '../../lib/newViewModalUi'

const SAFE_AREA =
  'pt-[max(1.75rem,calc(env(safe-area-inset-top,0px)+0.75rem))] pb-[max(1.25rem,calc(env(safe-area-inset-bottom,0px)+0.75rem))] pl-[max(1rem,calc(env(safe-area-inset-left,0px)+0.25rem))] pr-[max(1rem,calc(env(safe-area-inset-right,0px)+0.25rem))]'

export type NvModalFrameProps = {
  onClose: () => void
  title: string
  description?: string
  headerExtra?: ReactNode
  /** Merged after `nvPanelClass` (e.g. max-width overrides). */
  panelClassName?: string
  /**
   * When true, body is `flex flex-col overflow-hidden` so children can use
   * `flex-1 min-h-0 overflow-y-auto` + a shrink-0 footer.
   */
  bodyAsFlexColumn?: boolean
  /**
   * Short hubs (e.g. “choose an option” grids): panel height follows content instead of
   * filling `h-[90vh]`, and the body does not `flex-1` so the shell stays compact.
   */
  intrinsicHeight?: boolean
  /** Backdrop animation class (e.g. `modal-backdrop-enter` / `modal-backdrop-exit`). */
  enterExitClass?: string
  children: ReactNode
}

/** Same shell as New Show / New Song modals (`nvBackdropClass` + `nvPanelClass`). */
export function NvModalFrame({
  onClose,
  title,
  description,
  headerExtra,
  panelClassName,
  bodyAsFlexColumn = false,
  intrinsicHeight = false,
  enterExitClass,
  children,
}: NvModalFrameProps) {
  const titleId = 'nv-modal-frame-title'
  const descId = 'nv-modal-frame-desc'

  return (
    <div
      className={[nvBackdropClass, SAFE_AREA, enterExitClass].filter(Boolean).join(' ')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        className={[
          nvPanelClass,
          intrinsicHeight ? '!h-auto max-h-[min(90vh,100dvh)]' : '',
          panelClassName,
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
      >
        <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
          {headerExtra}
          <button type="button" onClick={onClose} className={nvIconButtonClass} aria-label="Close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="shrink-0 px-6 pt-6 pb-2 pr-28 sm:pr-32">
          <h2 id={titleId} className="text-xl font-bold text-gray-900 dark:text-white tracking-tight leading-tight">
            {title}
          </h2>
          {description ? (
            <p id={descId} className={nvAddModalSubtitleClass}>
              {description}
            </p>
          ) : null}
        </div>
        <div
          className={
            bodyAsFlexColumn
              ? 'flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-5 pt-0 sm:px-6 sm:pb-6'
              : intrinsicHeight
                ? 'shrink-0 overflow-y-auto overflow-x-hidden px-4 pb-5 pt-0 sm:px-6 sm:pb-6 scrollbar-hide'
                : 'min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-4 pb-5 pt-0 sm:px-6 sm:pb-6 scrollbar-hide'
          }
        >
          {children}
        </div>
      </div>
    </div>
  )
}
